/**
 * Sound event queue. Player (and later, weapons + guards) emit events that
 * the awareness system consumes once, and the debug overlay renders as a
 * fading flood-fill for a short window.
 *
 * Headless — no PixiJS. See CLAUDE.md architecture rule 4.
 */

export interface SoundEvent {
  /** Tile at which the sound was emitted. */
  x: number;
  y: number;
  /** Initial loudness budget for the flood. */
  loudness: number;
  /** Ticks since emission — used by the overlay for fade opacity. */
  age: number;
  /** Awareness has already processed this event. Kept around for viz decay. */
  consumed: boolean;
  /**
   * Emitter id — a numeric entity id, or the string 'player'. Awareness
   * skips events emitted by the guard consuming them so a guard's own
   * footsteps don't spike their own meter.
   */
  emitterId: number | 'player';
}

export class SoundQueue {
  readonly events: SoundEvent[] = [];

  /** Push a new event. Freshly emitted → age 0, unconsumed. */
  emit(x: number, y: number, loudness: number, emitterId: number | 'player' = 'player'): SoundEvent {
    const e: SoundEvent = { x, y, loudness, age: 0, consumed: false, emitterId };
    this.events.push(e);
    return e;
  }

  /** Yield the events awareness hasn't processed yet. */
  *fresh(): Iterable<SoundEvent> {
    for (const e of this.events) if (!e.consumed) yield e;
  }

  /** Mark every event consumed. Called by awareness after its pass. */
  markAllConsumed(): void {
    for (const e of this.events) e.consumed = true;
  }

  /** Advance visualization decay and evict events older than `maxAgeTicks`. */
  tickFade(maxAgeTicks: number): void {
    for (const e of this.events) e.age += 1;
    // In-place filter to avoid GC churn.
    let write = 0;
    for (let read = 0; read < this.events.length; read++) {
      const e = this.events[read]!;
      if (e.age <= maxAgeTicks) {
        this.events[write++] = e;
      }
    }
    this.events.length = write;
  }
}

/** Loudness for a footstep based on the mover's stance. */
export function footstepLoudness(stance: 'run' | 'walk' | 'crouch', cfg: {
  footstepRun: number;
  footstepWalk: number;
  footstepCrouch: number;
}): number {
  switch (stance) {
    case 'run':
      return cfg.footstepRun;
    case 'walk':
      return cfg.footstepWalk;
    case 'crouch':
      return cfg.footstepCrouch;
  }
}
